/**
 * The portal's half of the budget-assessment contract.
 *
 * The server owns what a question *is* — its key, category and answer type — and refuses
 * an answer to a key outside the assessment's frozen set. The portal owns what the
 * customer *reads*: wording, ordering, help text and translations. This module is that
 * half: the keys in the order they are asked, the input each one needs, and the option
 * vocabulary for the one multi-select.
 *
 * Option values are canonical English and are never translated — they are stored verbatim
 * and read by Ops and analytics, so a Portuguese customer's answer must still say
 * "Recent bereavement". Only the labels rendered next to them go through i18n.
 */

export type BudgetQuestionInput = 'currency' | 'multi_select' | 'free_text';

export interface BudgetQuestion {
  key: string;
  input: BudgetQuestionInput;
  /**
   * Where this question's wording lives under `budgetAssessment.questions.*`, when the
   * key alone would be ambiguous. Only `income_benefits` needs it: the `full` set asks
   * about pensions and maintenance separately, so the copy that tells a `short` customer
   * to fold them in here would have them counted twice.
   */
  copyKey?: string;
}

/** The i18n suffix carrying a question's label and hint. */
export const copyKeyFor = (question: BudgetQuestion) => question.copyKey ?? question.key;

const money = (key: string): BudgetQuestion => ({ key, input: 'currency' });

/**
 * Asked last at every depth, and in this order — the detail elaborates the options
 * above it, an ordering the server's bank asserts as an invariant.
 */
const SITUATION_QUESTIONS: readonly BudgetQuestion[] = [
  { key: 'situation_circumstances', input: 'multi_select' },
  { key: 'situation_detail', input: 'free_text' },
];

/** The server's `medium` set, in its order. */
const MEDIUM_QUESTIONS: readonly BudgetQuestion[] = [
  ...['income_salary', 'income_benefits'].map(money),
  ...['expenditure_rent_or_mortgage', 'expenditure_utilities', 'expenditure_food', 'expenditure_travel'].map(money),
  money('priority_debt_total'),
  ...SITUATION_QUESTIONS,
];

/**
 * The server's `high` set, in its order — income and expenditure itemised, and the
 * priority debts asked one by one rather than as a single total.
 */
const HIGH_QUESTIONS: readonly BudgetQuestion[] = [
  money('income_salary'),
  { ...money('income_benefits'), copyKey: 'income_benefits_itemised' },
  ...['income_pension', 'income_maintenance'].map(money),
  ...['expenditure_rent_or_mortgage', 'expenditure_utilities', 'expenditure_food', 'expenditure_travel'].map(money),
  money('expenditure_childcare'),
  ...[
    'priority_debt_council_tax',
    'priority_debt_hmrc',
    'priority_debt_child_maintenance',
    'priority_debt_court_fines',
    'priority_debt_loans',
    'priority_debt_credit_cards',
    'priority_debt_bnpl',
    'priority_debt_overdraft',
  ].map(money),
  ...SITUATION_QUESTIONS,
];

/**
 * One turn of the conversation: a single question, or a themed group answered together.
 *
 * Grouping exists for depth. Nineteen questions put one at a time is nineteen taps to
 * reach a summary, where four fields under "what comes in each month" is one — and a
 * customer entering related figures side by side can see them add up. A group's fields
 * need their own short labels (`budgetAssessment.questions.*.field`), since the
 * conversational label belongs to the intro bubble above them.
 */
export interface BudgetStep {
  /**
   * Suffix under `budgetAssessment.sections.*` holding the intro that opens the group.
   * Absent on a single-question step: its own label is the intro.
   */
  sectionKey?: string;
  questions: readonly BudgetQuestion[];
}

/** Each question its own step — the one-at-a-time flow, as a degenerate grouping. */
const singly = (questions: readonly BudgetQuestion[]): readonly BudgetStep[] =>
  questions.map((question) => ({ questions: [question] }));

const pick = (keys: readonly string[]): readonly BudgetQuestion[] =>
  keys.map((key) => {
    const question = HIGH_QUESTIONS.find((q) => q.key === key);
    if (!question) throw new Error(`Unknown budget question '${key}'`);
    return question;
  });

/**
 * The `full` tier's steps. Priority debts are split from credit commitments rather than
 * asked as one block of eight: it is the distinction the assessment itself draws, and it
 * keeps a step to four fields.
 *
 * The two situation questions stay one at a time. Circumstances is a long grouped
 * multi-select that needs the width, and the detail question must follow it alone — it is
 * asked only when a circumstance was flagged, and is answered on the customer's behalf
 * otherwise.
 */
const FULL_STEPS: readonly BudgetStep[] = [
  { sectionKey: 'income', questions: pick(['income_salary', 'income_benefits', 'income_pension', 'income_maintenance']) },
  {
    sectionKey: 'essentials',
    questions: pick([
      'expenditure_rent_or_mortgage',
      'expenditure_utilities',
      'expenditure_food',
      'expenditure_travel',
      'expenditure_childcare',
    ]),
  },
  {
    sectionKey: 'priorityDebts',
    questions: pick([
      'priority_debt_council_tax',
      'priority_debt_hmrc',
      'priority_debt_child_maintenance',
      'priority_debt_court_fines',
    ]),
  },
  {
    sectionKey: 'creditCommitments',
    questions: pick([
      'priority_debt_loans',
      'priority_debt_credit_cards',
      'priority_debt_bnpl',
      'priority_debt_overdraft',
    ]),
  },
  ...singly(SITUATION_QUESTIONS),
];

/**
 * What each tier asks, mirroring the schema's default `questionSet` per tier
 * (`short` → `medium`, `full` → `high`).
 *
 * The tier→set mapping itself lives in journey config the portal is deliberately not
 * allowed to read, so this is a mirror rather than a read. An operator who overrides
 * `questionSet` directly breaks the mirror, and the first out-of-set key 404s into the
 * page's dead-end message rather than looping — the same exposure both tiers already had.
 */
const STEPS_BY_TIER: Record<string, readonly BudgetStep[]> = {
  short: singly(MEDIUM_QUESTIONS),
  full: FULL_STEPS,
};

/**
 * The steps to put for a tier, or null for one this portal has no wording for.
 *
 * Null is not currently reachable — the server's `Tier` is exactly these two — but a
 * third tier added server-side would otherwise render as a silently truncated set that
 * offers Finish and dead-ends at completion. Refusing it is the safe failure.
 */
export function stepsForTier(tier: string): readonly BudgetStep[] | null {
  return STEPS_BY_TIER[tier] ?? null;
}

/**
 * Answered as a sum of amounts — whether the step holds one figure or several.
 *
 * Distinct from `isGroupedStep`, which is about how the step is *asked*. They were being
 * asserted separately at three sites, which is how the shape drifted once already.
 */
export const isMoneyStep = (step: BudgetStep) => step.questions.every((q) => q.input === 'currency');

/**
 * Asked as a block of labelled rows rather than one control.
 *
 * Every grouped step is a money step — the grouped renderer draws amount inputs and nothing
 * else — and the test over `STEPS_BY_TIER` is what holds the two together.
 */
export const isGroupedStep = (step: BudgetStep) => step.questions.length > 1;

/** Every question across the steps, in the order they are asked. */
export const questionsOf = (steps: readonly BudgetStep[]): readonly BudgetQuestion[] =>
  steps.flatMap((step) => step.questions);

/** Selecting this clears every other circumstance, and vice versa. */
export const SITUATION_NONE = 'None of these';

export interface SituationOptionGroup {
  /** Suffix under `budgetAssessment.situation.groups.*`. */
  labelKey: string;
  /** `value` is sent to the server; `labelKey` sits under `budgetAssessment.situation.options.*`. */
  options: { value: string; labelKey: string }[];
}

export const SITUATION_OPTION_GROUPS: readonly SituationOptionGroup[] = [
  {
    labelKey: 'health',
    options: [
      { value: 'Physical health condition', labelKey: 'physicalHealth' },
      { value: 'Mental health condition', labelKey: 'mentalHealth' },
      { value: 'Serious or terminal illness', labelKey: 'seriousIllness' },
      { value: 'Disability', labelKey: 'disability' },
      { value: 'Sight or hearing loss', labelKey: 'sensoryLoss' },
      { value: 'Memory or cognitive difficulties', labelKey: 'cognitive' },
      { value: 'Addiction or substance use', labelKey: 'addiction' },
      { value: 'Gambling harm', labelKey: 'gambling' },
    ],
  },
  {
    labelKey: 'lifeEvents',
    options: [
      { value: 'Recent bereavement', labelKey: 'bereavement' },
      { value: 'Job loss or reduced hours', labelKey: 'jobLoss' },
      { value: 'Relationship breakdown or divorce', labelKey: 'relationshipBreakdown' },
      { value: 'Caring for someone', labelKey: 'caring' },
      { value: 'Pregnancy or a new baby', labelKey: 'newBaby' },
      { value: 'Domestic or economic abuse', labelKey: 'abuse' },
      { value: 'Recently left hospital or prison', labelKey: 'leftInstitution' },
    ],
  },
  {
    labelKey: 'financialResilience',
    options: [
      { value: 'Income recently dropped', labelKey: 'incomeDropped' },
      { value: 'Struggling with other debts or bills', labelKey: 'otherDebts' },
      { value: 'Sudden large or unexpected expense', labelKey: 'unexpectedExpense' },
    ],
  },
  {
    labelKey: 'understanding',
    options: [
      { value: 'Difficulty reading or understanding letters', labelKey: 'readingDifficulty' },
      { value: 'Language difficulties', labelKey: 'languageDifficulty' },
      { value: 'Not confident managing money', labelKey: 'moneyConfidence' },
    ],
  },
  {
    labelKey: 'other',
    options: [{ value: SITUATION_NONE, labelKey: 'noneOfThese' }],
  },
];

/**
 * The first step holding a question with no answer line, or -1 when all are answered.
 *
 * A part-answered group reopens whole: its fields prefill from what is stored, so the
 * customer sees the figures they already gave alongside the ones still missing.
 */
export function firstUnansweredStep(steps: readonly BudgetStep[], answeredKeys: readonly string[]): number {
  return steps.findIndex((step) => step.questions.some((question) => !answeredKeys.includes(question.key)));
}

/** Values chosen for a multi-select answer, ignoring anything of the wrong shape. */
export function selectedOptions(answerValue: unknown): string[] {
  return Array.isArray(answerValue) ? answerValue.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Whether a question should be answered on the customer's behalf rather than asked.
 *
 * Only `situation_detail` qualifies: asking how their circumstances affect their finances
 * makes no sense to someone who flagged none. The server still requires a line for every
 * key in the set, so the caller submits an empty string — a captured "nothing to add"
 * rather than a question nobody answered.
 *
 * Returns false once something has actually been written, so a customer who answers, then
 * goes back and clears their circumstances, keeps the words they wrote.
 */
export function isAnsweredOnBehalf(
  key: string,
  answers: readonly { question_key: string; answer_value: unknown }[],
): boolean {
  if (key !== 'situation_detail') return false;
  const detail = answers.find((line) => line.question_key === 'situation_detail');
  if (typeof detail?.answer_value === 'string' && detail.answer_value.trim() !== '') return false;
  const circumstances = answers.find((line) => line.question_key === 'situation_circumstances');
  if (!circumstances) return false;
  return !hasFlaggedCircumstance(circumstances.answer_value);
}

function hasFlaggedCircumstance(answerValue: unknown): boolean {
  const chosen = selectedOptions(answerValue);
  return chosen.length > 0 && !(chosen.length === 1 && chosen[0] === SITUATION_NONE);
}

/**
 * Whether a question already holding an answer must still be put to the customer.
 *
 * Only `situation_detail`, and only where it was answered on their behalf: a customer who
 * flagged nothing gets an empty "nothing to add", and may then go back and flag a
 * circumstance. Every key now has a line, so nothing would otherwise re-open it — and an
 * unasked blank would stand as the recorded answer on the one question about vulnerability.
 *
 * Callers must not re-ask a question they have already shown, or a deliberately blank
 * answer would reopen it for ever.
 */
export function needsReasking(
  key: string,
  answers: readonly { question_key: string; answer_value: unknown }[],
): boolean {
  if (key !== 'situation_detail') return false;
  const detail = answers.find((line) => line.question_key === 'situation_detail');
  if (!detail) return false;
  const written = typeof detail.answer_value === 'string' ? detail.answer_value.trim() : '';
  if (written !== '') return false;
  const circumstances = answers.find((line) => line.question_key === 'situation_circumstances');
  return circumstances != null && hasFlaggedCircumstance(circumstances.answer_value);
}
